import {
  forwardRef,
  useState,
  useContext,
  PropsWithChildren,
  FC,
  ReactNode,
  ComponentProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { colorsButton, shadowOverlay } from 'app/colors';
import { borderRadii, gutters } from 'app/spacing';
import IconButton from '__components/Kizen/IconButton';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import Draggable from 'pages/AutomationEngine/Draggable';
import { StepCard, StepCardProps } from './StepCard';
import { flushUiChangesHack } from '../utilities';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { StepCardOld } from './StepCardOld';

const StepCardWithHandleWrapper = styled.div<{
  grabbable?: boolean;
  showHandle?: boolean;
  showPlaceholder?: boolean;
}>`
  position: relative;
  .Handle {
    width: 18px;
    height: 25px;
    ${shadowOverlay}
    // Would use visibility, but hidden elements don't
    // respond to events e.g. hover, which is important here.
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateX(-100%) translateY(-50%);
    ${({ grabbable }) =>
      grabbable &&
      css`
        cursor: grab;
      `}
    background: var(--color-background-white);
    padding: ${gutters.spacing(1)}px;
    border-top-left-radius: ${borderRadii.small};
    border-bottom-left-radius: ${borderRadii.small};
  }
  &:hover .Handle {
    opacity: 1;
  }
  ${({ showHandle }) =>
    showHandle &&
    css`
      .Handle {
        opacity: 1;
      }
    `}
  ${({ showHandle }) =>
    showHandle === false &&
    css`
      &&:hover .Handle {
        opacity: 0;
      }
    `}
    ${({ showPlaceholder }) =>
    showPlaceholder &&
    css`
      & > *,
      .Handle,
      &:hover .Handle {
        opacity: 0;
      }
    `}
`;

type StepCardHandleProps = PropsWithChildren<{
  icon?: ReactNode;
  onClick?: () => void;
  tooltipTitle?: string;
  grabbable?: boolean;
  showHandle?: boolean;
  showPlaceholder?: boolean;
}>;

export const StepCardHandle = forwardRef<HTMLDivElement, StepCardHandleProps>(
  ({ children, showHandle, icon, onClick, tooltipTitle, ...props }, ref) => {
    return (
      <StepCardWithHandleWrapper ref={ref} showHandle={showHandle} {...props}>
        {children}
        <IconButton
          title={tooltipTitle}
          onClick={onClick}
          className="Handle"
          sizing="dense"
          color={
            showHandle === true
              ? 'var(--color-button-tertiary-hover)'
              : colorsButton.iconGray
          }
        >
          {icon}
        </IconButton>
      </StepCardWithHandleWrapper>
    );
  }
);

StepCardHandle.displayName = 'StepCardWithHandle';

export type DraggableStepCardProps = StepCardProps &
  ComponentProps<typeof Draggable> & {
    showHandle?: boolean;
    isSmartConnectorFlow?: boolean;
    title?: string;
  };

export const DraggableStepCard: FC<
  DraggableStepCardProps & { color?: string }
> = ({
  scale,
  position,
  onStart,
  onStop,
  panel,
  showHandle,
  style = undefined,
  ...props
}) => {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [dragStopping, setDragStopping] = useState(false);

  const { historyId, executionMode } = useContext(ExecutionHistoryContext);
  const disableFeatures = historyId || executionMode;

  const CardComponent = props.isSmartConnectorFlow ? StepCardOld : StepCard;
  return disableFeatures ? (
    <CardComponent panel={panel} {...props} disableMenu />
  ) : (
    <Draggable
      scale={scale}
      position={position}
      onStart={(ev, data) => {
        setDragging(true);
        if (onStart) {
          onStart(ev, data);
        }
        setDragStopping(false);
      }}
      onStop={(ev, data) => {
        setDragging(false);
        if (onStop) onStop(ev, data);

        // TODO this solves an issue where dropping a step during a move would very briefly
        // show the step in its original position before painting the updated agentic workflow flow.
        // Instead we hide it temporarily, which seems to be imperceptible to the user.
        // We'll need a much better way to synchronize changes between state and react-flow,
        // but that will be a significanly more complex project.  Luckily these hacks aren't
        // really critical, and nothing burns down if they fail: just some UI flicker.
        const workaround = async () => {
          setDragStopping(true);
          await flushUiChangesHack(); // This waits for the drop to occur (see use of flushUiChangesHack() in AddButton)
          await flushUiChangesHack(); // And this waits for the agentic workflow flow to paint with the new state.
          setDragStopping(false);
        };
        workaround();
      }}
      style={style}
    >
      <StepCardHandle
        grabbable
        icon={
          <IconSizing size="15px">
            <Icon icon="reorder" />
          </IconSizing>
        }
        showPlaceholder={dragStopping}
        showHandle={dragging || showHandle}
        tooltipTitle={t('Move Item')}
      >
        <CardComponent panel={panel} {...props} dragging={dragging} />
      </StepCardHandle>
    </Draggable>
  );
};

export default DraggableStepCard;
